import { useMemo } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BarChart3, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useProfile } from '@/api/profile'
import { useAnalyticsDrilldown } from '@/api/analyticsDrilldown'
import { VoiceProvider } from '@/contexts/VoiceProvider'
import { useSSEHandlers } from '@/hooks/useSSEHandlers'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { AuthBootProvider } from '@/providers/AuthBootProvider'
import { ManagerBootProvider } from '@/providers/ManagerBootProvider'
import { GlobalCallModal } from '@/components/dashboard/GlobalCallModal'
import { InCallWidget } from '@/components/dashboard/InCallWidget'
import { InactiveTabOverlay } from '@/components/dashboard/InactiveTabOverlay'
import { CallsDrilldownTable } from '@/components/manager/analytics/drilldown/CallsDrilldownTable'
import { SmsDrilldownTable } from '@/components/manager/analytics/drilldown/SmsDrilldownTable'
import { AppointmentsDrilldownTable } from '@/components/manager/analytics/drilldown/AppointmentsDrilldownTable'
import { VoicemailsDrilldownTable } from '@/components/manager/analytics/drilldown/VoicemailsDrilldownTable'
import { DrilldownPagination } from '@/components/manager/analytics/drilldown/DrilldownPagination'
import { formatRelativeDate } from '@/components/manager/analytics/drilldown/formatters'
import type {
  DrilldownAppointmentRow,
  DrilldownCallRow,
  DrilldownMetric,
  DrilldownSmsRow,
  DrilldownVoicemailRow,
} from '@/types/analyticsDrilldown'

const ALLOWED_METRICS: DrilldownMetric[] = [
  'calls_total',
  'talk_time',
  'calls_inbound',
  'calls_outbound',
  'missed',
  'sms_sent',
  'appointments_created',
  'voicemails_listened',
]

// Mapping metric drilldown → clé i18n du label affichable.
// Les clés `cards.*` portent les noms historiques (talk_time_seconds, missed_total)
// alors que le metric API est court (talk_time, missed) — on évite un alias i18n
// en redirigeant ici, ce qui garde i18n minimal et cohérent avec TeamTotalsCards.
const METRIC_LABEL_KEY: Record<DrilldownMetric, string> = {
  calls_total: 'manager.analytics.cards.calls_total',
  talk_time: 'manager.analytics.cards.talk_time_seconds',
  calls_inbound: 'manager.analytics.cards.calls_inbound',
  calls_outbound: 'manager.analytics.cards.calls_outbound',
  missed: 'manager.analytics.cards.missed_total',
  sms_sent: 'manager.analytics.cards.sms_sent',
  appointments_created: 'manager.analytics.cards.appointments_created',
  voicemails_listened: 'manager.analytics.cards.voicemails_listened',
}

export function AnalyticsDrilldownPage() {
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { data: profile, isLoading: profileLoading } = useProfile()
  useSSEHandlers()

  // Supervisors follow the team too (analytics, live, history).
  if (!profileLoading && profile?.role === 'AGENT' && profile?.access?.role !== 'supervisor') {
    return <Navigate to={`/${locale}/dashboard`} replace />
  }

  return (
    <VoiceProvider>
      <AuthBootProvider>
        <ManagerBootProvider>
          <DrilldownContent />
        </ManagerBootProvider>
      </AuthBootProvider>
      <GlobalCallModal />
      <InCallWidget />
      <InactiveTabOverlay />
    </VoiceProvider>
  )
}

function DrilldownContent() {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile } = useProfile()

  const metricParam = searchParams.get('metric') as DrilldownMetric | null
  const metric: DrilldownMetric =
    metricParam && ALLOWED_METRICS.includes(metricParam) ? metricParam : 'calls_total'
  const userIdParam = searchParams.get('user_id')
  const userId = userIdParam ? Number(userIdParam) : undefined
  const period = searchParams.get('period') ?? 'cette_semaine'
  const page = Math.max(1, Number(searchParams.get('page') ?? '1'))

  const { data, isLoading, isFetching, error } = useAnalyticsDrilldown(profile?.company, {
    metric,
    user_id: userId,
    period,
    page,
    page_size: 25,
  })

  const metricLabel = t(METRIC_LABEL_KEY[metric])
  const scopeLabel = useMemo(() => {
    if (data?.scope.type === 'agent' && data.scope.agent) return data.scope.agent.name
    return t('manager.analytics.drilldown.scopeTeam')
  }, [data, t])
  const periodLabel = t(`manager.analytics.periods.${period}`, period)

  useDocumentTitle(`${metricLabel} · ${scopeLabel}`)

  const pageBg = darkMode ? 'bg-slate-900' : 'bg-slate-50'
  const titleColor = darkMode ? 'text-white' : 'text-slate-900'
  const subtitleColor = darkMode ? 'text-slate-400' : 'text-slate-500'

  const changePage = (next: number) => {
    const sp = new URLSearchParams(searchParams)
    sp.set('page', String(next))
    setSearchParams(sp, { replace: false })
  }

  const goBackToAnalytics = () => {
    // Préserve la période choisie sur la vue parent.
    navigate(`/${locale}/manager?view=analytics&period=${period}`)
  }

  return (
    <div className={cn('h-screen flex flex-col', pageBg)}>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-5 max-w-[1400px] mx-auto">
          {/* Breadcrumb + bouton retour */}
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={goBackToAnalytics}
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md font-medium',
                'transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                darkMode
                  ? 'text-slate-300 hover:bg-slate-800'
                  : 'text-slate-700 hover:bg-slate-100',
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('manager.analytics.drilldown.backToAnalytics')}
            </button>
            <ChevronRight
              className={cn('h-3.5 w-3.5', darkMode ? 'text-slate-600' : 'text-slate-400')}
            />
            <span className={cn('flex items-center gap-1.5', subtitleColor)}>
              <BarChart3 className="h-3.5 w-3.5" />
              {t('manager.tabs.analytics')}
            </span>
            <ChevronRight
              className={cn('h-3.5 w-3.5', darkMode ? 'text-slate-600' : 'text-slate-400')}
            />
            <span className={cn('font-medium', titleColor)}>{metricLabel}</span>
          </div>

          {/* Header */}
          <div>
            <h1 className={cn('text-2xl font-semibold', titleColor)}>
              {metricLabel}
            </h1>
            <p className={cn('text-sm mt-1', subtitleColor)}>
              {data
                ? t('manager.analytics.drilldown.contextLine', {
                    scope: scopeLabel,
                    period: periodLabel,
                    start: formatRelativeDate(data.period.start, locale),
                    end: formatRelativeDate(data.period.end, locale),
                    total: data.total,
                  })
                : t('manager.analytics.drilldown.loading')}
            </p>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-12 rounded-lg animate-pulse',
                    darkMode ? 'bg-slate-800' : 'bg-slate-200/70',
                  )}
                  style={{ animationDelay: `${i * 50}ms` }}
                />
              ))}
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div
              className={cn(
                'rounded-xl border p-6 text-center',
                darkMode ? 'border-rose-500/30 bg-rose-500/10' : 'border-rose-200 bg-rose-50',
              )}
            >
              <p className="text-rose-600 dark:text-rose-400 font-medium">
                {error instanceof Error
                  ? error.message
                  : t('manager.analytics.drilldown.loadError')}
              </p>
            </div>
          )}

          {/* Data */}
          {data && !isLoading && (
            <>
              {data.results.length === 0 ? (
                <div
                  className={cn(
                    'rounded-xl border p-10 text-center',
                    darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
                  )}
                >
                  <p className={cn('text-sm', subtitleColor)}>
                    {t('manager.analytics.drilldown.emptyState')}
                  </p>
                </div>
              ) : (
                <>
                  {data.kind === 'call' && (
                    <CallsDrilldownTable
                      rows={data.results as DrilldownCallRow[]}
                      showCallAction={metric === 'missed'}
                      showStatus={metric === 'calls_total' || metric === 'talk_time' || metric === 'missed'}
                      companyId={profile?.company ?? 0}
                      locale={locale}
                      darkMode={darkMode}
                    />
                  )}
                  {data.kind === 'sms' && (
                    <SmsDrilldownTable
                      rows={data.results as DrilldownSmsRow[]}
                      locale={locale}
                      darkMode={darkMode}
                    />
                  )}
                  {data.kind === 'appointment' && (
                    <AppointmentsDrilldownTable
                      rows={data.results as DrilldownAppointmentRow[]}
                      locale={locale}
                      darkMode={darkMode}
                    />
                  )}
                  {data.kind === 'voicemail' && (
                    <VoicemailsDrilldownTable
                      rows={data.results as DrilldownVoicemailRow[]}
                      locale={locale}
                      darkMode={darkMode}
                    />
                  )}

                  <div className={cn('pt-2', isFetching && 'opacity-70')}>
                    <DrilldownPagination
                      page={data.page}
                      totalPages={data.total_pages}
                      total={data.total}
                      pageSize={data.page_size}
                      onChange={changePage}
                      darkMode={darkMode}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
